import React from "react";
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  ClockIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const Field = ({ label, value, icon: Icon }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
    <span className="text-sm font-medium text-gray-900">{value || "—"}</span>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 bg-gray-50">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, transparent)" }}
      >
        <Icon className="w-4 h-4" style={{ color: "var(--app-primary-color, #26256b)" }} />
      </div>
      <span className="text-sm font-semibold text-gray-800">{title}</span>
    </div>
    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
      {children}
    </div>
  </div>
);

const OwnershipTab = ({ ownership }) => {
  if (!ownership) return null;

  const initials = (ownership.name ?? "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6">

      {/* Owner summary */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
          style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
        >
          {initials}
        </div>
        <div>
          <p className="text-base font-semibold text-gray-900">{ownership.name || "—"}</p>
          <p className="text-sm text-gray-500">{ownership.email || "—"}</p>
        </div>
        {ownership.status && (
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold border ${
            ownership.status.toLowerCase() === "active"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-gray-100 text-gray-600 border-gray-200"
          }`}>
            {ownership.status}
          </span>
        )}
      </div>

      {/* Owner Profile */}
      <Section icon={UserIcon} title="Owner Profile">
        <Field icon={UserIcon}    label="Full Name"   value={ownership.name} />
        <Field icon={EnvelopeIcon} label="Email"      value={ownership.email} />
        <Field icon={ShieldCheckIcon} label="Role"    value={ownership.role} />
        <Field icon={ClockIcon}   label="Last Login"  value={
          ownership.last_login
            ? new Date(ownership.last_login).toLocaleString()
            : null
        } />
      </Section>

      {/* Employment Details */}
      <Section icon={BuildingOfficeIcon} title="Employment Details">
        <Field icon={BuildingOfficeIcon} label="Department"     value={ownership.department} />
        <Field icon={BriefcaseIcon}      label="Reporting To"   value={ownership.reporting_to} />
        <Field icon={CalendarDaysIcon}   label="Date of Joining" value={
          ownership.date_of_joining
            ? new Date(ownership.date_of_joining).toLocaleDateString()
            : null
        } />
      </Section>

    </div>
  );
};

export default OwnershipTab;
